import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "600px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const RideShareDriverNewRideRouteView = ({ initialPoints, onRouteSend, driverLocation }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const [currentPosition, setCurrentPosition] = useState(driverLocation || defaultCenter);
  const [origin, setOrigin] = useState(initialPoints?.origin || driverLocation || defaultCenter);
  const [waypoint, setWaypoint] = useState(initialPoints?.waypoint || null);
  const [destination, setDestination] = useState(
    initialPoints?.destination || { lat: -17.8350, lng: 31.0550 }
  );
  const [directions, setDirections] = useState(null);
  const [eta, setEta] = useState("");

  const mapRef = useRef();

  // Get current position
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCurrentPosition(coords);
        if (!initialPoints?.origin && !driverLocation) setOrigin(coords);
      });
    }
  }, [initialPoints, driverLocation]);

  // Fetch directions safely
  useEffect(() => {
    if (!isLoaded || !window.google || !origin || !destination) return;

    // Validate coordinates
    if (!origin.lat || !origin.lng || !destination.lat || !destination.lng) {
      console.error("Invalid coordinates:", { origin, destination });
      return;
    }

    console.log("Fetching directions from:", origin, "to:", destination);
    const service = new window.google.maps.DirectionsService();
    const waypts = waypoint && waypoint.lat && waypoint.lng ? [{ location: waypoint, stopover: true }] : [];

    service.route(
      {
        origin,
        destination,
        waypoints: waypts,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false
      },
      (result, status) => {
        console.log("Directions result:", status);
        if (status === "OK") {
          console.log("Route found with", result.routes[0].legs.length, "legs");
          console.log("Route overview polyline:", result.routes[0].overview_polyline);
          setDirections(result);
          const totalSeconds = result.routes[0].legs.reduce(
            (sum, leg) => sum + leg.duration.value,
            0
          );
          setEta(`${Math.ceil(totalSeconds / 60)} min`);
        } else {
          console.error("Directions request failed:", status);
          console.error("Origin:", origin, "Destination:", destination);
          console.error("Possible reasons: Invalid coordinates, no route available, or API quota exceeded");
        }
      }
    );
  }, [isLoaded, origin, waypoint, destination]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <h2>Driver New Ride Route</h2>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={currentPosition}
        onLoad={(map) => (mapRef.current = map)}
      >
        <Marker position={currentPosition} title="You" label="You" />
        {origin && <Marker position={origin} title="Origin" label="O" />}
        {waypoint && <Marker position={waypoint} title="Waypoint" label="W" />}
        {destination && <Marker position={destination} title="Destination" label="D" />}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: { 
                strokeColor: "#800080", 
                strokeWeight: 5,
                strokeOpacity: 0.8
              },
              preserveViewport: false,
              draggable: false
            }}
          />
        )}
      </GoogleMap>

      <div style={{ marginTop: "10px" }}>
        <p>
          <strong>ETA:</strong> {eta || "N/A"}
        </p>
        <button
          onClick={() =>
            onRouteSend?.({ origin, waypoint, destination, directions, eta })
          }
        >
          Send Route
        </button>
      </div>
    </div>
  );
};

export default RideShareDriverNewRideRouteView;